// ============================================================================
// apps/web · konzept-prefill-utils — Pure helpers for prefill UX
// No browser APIs. No React. Fully testable in Node/Vitest.
// ============================================================================

// ─── Form-füllbare Werte (aus der API zurückgegeben) ─────────────────────────

export interface PrefillFormData {
  bundesland: string | null;
  kwp: number | null;
  jahresverbrauchKwh: number | null;
  strompreisCtProKwh: number | null;
  einspeisetarifCtProKwh: number | null;
  investitionAEur: number | null;
  investitionBEur: number | null;
  speicherKwh: number | null;
  notstromFaehig: boolean | null;
  empfehlung: string | null;
}

export interface StorageEstimateInfo {
  /** Fields missing to compute storage eval */
  missingFields: string[];
  /** All assumptions used in estimation */
  assumptions: string[];
  /** true when speicherKwh > 0 but speicherInvestEur is missing */
  missingInvestment: boolean;
  /** true when speicher > 0 in saved input */
  hasStorage: boolean;
}

export interface StorageEvalData {
  engineStatus: string;
  confidenceScore: number;
  missingData?: string[];
  data: {
    verdict: string;
    summary: string;
    requiresHumanReview: true;
    canApprove: false;
    detail: {
      storagePaybackYears: number | null;
      additionalNpvEur: number | null;
      isViable: boolean;
      guardrail: string;
    } | null;
  } | null;
}

export interface PrefillResultData {
  source: {
    projectRef?: string | null;
    customer?: { name: string; type: string } | null;
    project?: {
      name: string;
      address: string;
      buildingType: string;
      notes?: string | null;
    } | null;
  };
  /** Merged form values ready to fill form fields. */
  formData: PrefillFormData;
  availableFields: string[];
  missingFields: string[];
  assumptions: string[];
  /** true if a persisted ConceptProjectInput row exists for this project. */
  hasSavedInput: boolean;
  /** Storage estimation metadata (B5) */
  storageEstimate?: StorageEstimateInfo | null;
  /** Full storage evaluation result (B5) — null when data insufficient */
  storageEval?: StorageEvalData | null;
}

// ─── Human-readable labels ────────────────────────────────────────────────────

export const FIELD_LABELS: Record<string, string> = {
  "customer.name": "Kundenname",
  "customer.type": "Kundentyp",
  "project.name": "Projektname",
  "project.address": "Adresse",
  "project.buildingType": "Gebäudetyp",
  "project.notes": "Projektnotizen",
  "input.kwp": "Anlage (kWp)",
  "input.jahresverbrauchKwh": "Jahresverbrauch (kWh/a)",
  "input.strompreisCtProKwh": "Strompreis (ct/kWh)",
  "input.einspeisetarifCtProKwh": "Einspeisetarif (ct/kWh)",
  "input.investitionAEur": "Investition Variante A (€)",
  "input.investitionBEur": "Investition Variante B (€)",
  "input.speicherKwh": "Speichergröße (kWh)",
  "input.notstromFaehig": "Notstrom-fähig",
  "input.empfehlung": "Empfehlung (A/B)",
};

export const MISSING_FIELD_LABELS: Record<string, string> = {
  "consumption.strompreis": "Strompreis (ct/kWh)",
  "consumption.einspeisetarif": "Einspeisetarif (ct/kWh)",
  "consumption.jahresverbrauch": "Jahresverbrauch (kWh/a)",
  "anlage.kwp": "Anlage (kWp) — aus Planung / Dach-Designer",
  empfehlung: "Varianten-Empfehlung (A oder B — Mensch entscheidet)",
  // Legacy keys kept for backwards compat with earlier prefill-source responses
  anlage: "Anlage (kWp, Module, Ausrichtung, Jahresertrag)",
  consumption: "Verbrauch (kWh/a, Strompreis ct/kWh, Einspeisevergütung)",
  varianteA: "Variante A — Investition & Wirtschaftlichkeit",
  varianteB: "Variante B — Investition & Wirtschaftlichkeit",
};

// ─── Stadtname → Bundesland ───────────────────────────────────────────────────

const CITY_MAP: Record<string, string> = {
  Wien: "Wien",
  Vienna: "Wien",
  Graz: "Steiermark",
  Linz: "Oberösterreich",
  Salzburg: "Salzburg",
  Innsbruck: "Tirol",
  Klagenfurt: "Kärnten",
  Villach: "Kärnten",
  Spittal: "Kärnten",
  Bregenz: "Vorarlberg",
  Feldkirch: "Vorarlberg",
  Dornbirn: "Vorarlberg",
  Hohenems: "Vorarlberg",
  Eisenstadt: "Burgenland",
  "Wiener Neustadt": "Niederösterreich",
  Krems: "Niederösterreich",
  "Baden bei Wien": "Niederösterreich",
};

/**
 * PLZ → Bundesland (ganz Österreich, Heuristik nach Leitzahlen-Bereichen).
 * Wichtige Ausnahmen sind abgebildet:
 *   - Osttirol (Bezirk Lienz) 9900–9999 → Tirol, NICHT Kärnten
 *   - Vorarlberg 6700–6999, restliches 6xxx → Tirol
 * Echte Grenzfälle (einzelne Gemeinden mit Fremd-PLZ) existieren — das Ergebnis
 * ist deshalb immer nur eine VORBELEGUNG, die der Nutzer übersteuern kann.
 */
export function bundeslandFromPlz(plz: number): string | null {
  if (plz >= 1010 && plz <= 1239) return "Wien";
  if (plz >= 2000 && plz <= 3999) return "Niederösterreich";
  if (plz >= 4000 && plz <= 4999) return "Oberösterreich";
  if (plz >= 5000 && plz <= 5999) return "Salzburg";
  if (plz >= 6700 && plz <= 6999) return "Vorarlberg";
  if (plz >= 6000 && plz <= 6699) return "Tirol";
  if (plz >= 7000 && plz <= 7999) return "Burgenland";
  if (plz >= 8000 && plz <= 8999) return "Steiermark";
  if (plz >= 9900 && plz <= 9999) return "Tirol"; // Osttirol (Bezirk Lienz)
  if (plz >= 9000 && plz <= 9899) return "Kärnten";
  return null;
}

/**
 * Heuristically detects the Austrian Bundesland from a free-text address.
 * City names are checked longest-first (so "Wiener Neustadt" beats "Wien").
 * Falls keine Stadt matcht, greift das PLZ-Mapping (ganz Österreich).
 * Returns null when detection is uncertain — callers should show a selector.
 */
export function detectBundeslandFromAddress(address: string): string | null {
  if (!address) return null;

  // St. Pölten special-case (contains period or variants)
  if (address.includes("St.") && address.includes("Pölten")) return "Niederösterreich";
  if (address.includes("St Pölten") || address.includes("Sankt Pölten")) return "Niederösterreich";

  // City name substring match — sorted longest-first so "Wiener Neustadt" wins over "Wien"
  const sortedCities = Object.entries(CITY_MAP).sort((a, b) => b[0].length - a[0].length);
  for (const [city, bl] of sortedCities) {
    if (address.includes(city)) return bl;
  }

  // PLZ-Fallback (ganz Österreich, inkl. Osttirol-/Vorarlberg-Ausnahmen).
  // Bevorzugt "PLZ + Ortsname" (echte PLZ-Position), damit vierstellige
  // Hausnummern wie "Hauptstraße 1010" nicht fälschlich greifen.
  const zipBeforeCity = /\b([1-9][0-9]{3})\s+\p{L}/u.exec(address);
  const zipAnywhere = /\b([1-9][0-9]{3})\b/.exec(address);
  const zip = zipBeforeCity?.[1] ?? zipAnywhere?.[1];
  if (zip) {
    return bundeslandFromPlz(parseInt(zip, 10));
  }

  return null;
}

// ─── Helper: apply prefill form data to form state setters ───────────────────
// Returns list of field keys that were actually filled (for the banner).

export interface FormSetters {
  setKwp: (v: string) => void;
  setVerbrauch: (v: string) => void;
  setStrompreis: (v: string) => void;
  setEinspeisetarif: (v: string) => void;
  setInvestitionA: (v: string) => void;
  setInvestitionB: (v: string) => void;
  setSpeicherKwh: (v: string) => void;
  setNotstrom: (v: boolean) => void;
}

/**
 * Applies PrefillFormData to form setters, skipping fields that the user has
 * already manually touched (touchedFields Set).
 * Returns the list of human-readable field labels that were actually applied.
 */
export function applyPrefillToForm(
  data: PrefillFormData,
  setters: FormSetters,
  touchedFields: ReadonlySet<string>,
): string[] {
  const filled: string[] = [];

  if (data.kwp != null && !touchedFields.has("kwp")) {
    setters.setKwp(String(data.kwp));
    filled.push(FIELD_LABELS["input.kwp"]!);
  }
  if (data.jahresverbrauchKwh != null && !touchedFields.has("verbrauch")) {
    setters.setVerbrauch(String(data.jahresverbrauchKwh));
    filled.push(FIELD_LABELS["input.jahresverbrauchKwh"]!);
  }
  if (data.strompreisCtProKwh != null && !touchedFields.has("strompreis")) {
    setters.setStrompreis(String(data.strompreisCtProKwh));
    filled.push(FIELD_LABELS["input.strompreisCtProKwh"]!);
  }
  if (data.einspeisetarifCtProKwh != null && !touchedFields.has("einspeisetarif")) {
    setters.setEinspeisetarif(String(data.einspeisetarifCtProKwh));
    filled.push(FIELD_LABELS["input.einspeisetarifCtProKwh"]!);
  }
  if (data.investitionAEur != null && !touchedFields.has("investitionA")) {
    setters.setInvestitionA(String(data.investitionAEur));
    filled.push(FIELD_LABELS["input.investitionAEur"]!);
  }
  if (data.investitionBEur != null && !touchedFields.has("investitionB")) {
    setters.setInvestitionB(String(data.investitionBEur));
    filled.push(FIELD_LABELS["input.investitionBEur"]!);
  }
  if (data.speicherKwh != null && !touchedFields.has("speicherKwh")) {
    setters.setSpeicherKwh(String(data.speicherKwh));
    filled.push(FIELD_LABELS["input.speicherKwh"]!);
  }
  if (data.notstromFaehig != null && !touchedFields.has("notstrom")) {
    setters.setNotstrom(data.notstromFaehig);
    filled.push(FIELD_LABELS["input.notstromFaehig"]!);
  }
  return filled;
}
